import type { LayoutDocument } from "../layout/types.js";
import { validateLayout } from "../layout/validation.js";
import type { CustomDisciplineBehavior } from "./behavior.js";
import {
  isValidCustomDisciplineBehavior,
  normalizeBehaviorGraph,
} from "./behavior.js";
import type { CustomDisciplineGroupType } from "./types.js";
import { createCustomDisciplineGridPresentation } from "./grid-presentation.js";
import {
  createCustomWebToolbar,
  validateCustomWebToolbar,
  type CustomWebToolbar,
} from "./web-toolbar.js";
import {
  compileCustomDisciplineField,
  evaluateCustomDisciplineCell,
  evaluateCustomDisciplineActionClick,
  evaluateCustomDisciplineEvent,
  releaseCustomDisciplineBehaviorRuntime,
  type CustomDisciplineEvaluateInput,
} from "../custom-discipline-behavior/index.js";
export const CUSTOM_WEB_VIEW_KEY = "web_view";
export type CustomWebColumn = {
  key: string;
  label: string;
  fieldKey: string;
  width: number;
  visible: boolean;
  /** Independent from the editing grid; header sorting is disabled by default. */
  isSortable?: boolean;
  pin: "left" | "right" | null;
  behavior: CustomDisciplineBehavior | null;
  cellLayout: LayoutDocument | null;
  headerLayout: LayoutDocument | null;
  headerGroup?: string;
  tooltipLayout?: LayoutDocument | null;
};
export type CustomWebView = {
  version: 1;
  columns: CustomWebColumn[];
  presentation: LayoutDocument;
  liveOrder: CustomDisciplineBehavior | null;
  categoryOrder: CustomDisciplineBehavior | null;
  categoryColumns: string[];
  defaultMode: "LIVE" | "CATEGORY";
  followCurrentAthlete: boolean;
  /** Independent spectator controls; absent in views saved before toolbar administration. */
  toolbar?: CustomWebToolbar;
};
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));
/** A Display Editor preset matching the public legacy table, independent of the editing grid. */
export function createLegacyCustomWebPresentation(): LayoutDocument {
  const document = createCustomDisciplineGridPresentation("Web");
  document.scene.children = document.scene.children.filter((n) =>
    [
      "column-header",
      "cell",
      "active-row",
      "hover-row",
      "hover-active-row",
    ].includes(n.id),
  );
  for (const node of document.scene.children) {
    if (node.id === "column-header") {
      node.height = 60;
      node.style = {
        backgroundColor: "#0056B3",
        color: "#ffffff",
        fontWeight: "700",
        fontSize: 19,
        borderRight: "1px solid #e0e0e0",
        padding: "0 4px",
      };
    } else if (node.id === "cell") {
      node.style = {
        fontSize: 18,
        color: "#000000",
        borderRight: "1px solid #e0e0e0",
      };
    } else if (node.id === "active-row" || node.id === "hover-active-row") {
      node.style = {
        borderTop: "2px solid red",
        borderBottom: "2px solid red",
      };
    } else node.style = {};
  }
  const palette = [
    "rgb(255, 235, 242)",
    "rgb(230, 248, 255)",
    "rgb(230, 255, 230)",
    "rgb(255, 245, 235)",
    "rgb(250, 238, 250)",
    "rgb(255, 250, 242)",
    "rgb(238, 252, 255)",
    "rgb(255, 242, 248)",
  ];
  palette.forEach((backgroundColor, index) =>
    document.scene.children.push({
      id: `category-color:${index}`,
      label: `Catégorie · couleur ${index + 1}`,
      kind: "primitive",
      primitive: "text",
      text: `Catégorie · couleur ${index + 1}`,
      x: 24,
      y: 20 + (5 + index) * 76,
      width: 552,
      height: 52,
      zIndex: 0,
      style: { backgroundColor },
    }),
  );
  document.meta.canvas.height = 20 + document.scene.children.length * 76;
  return document;
}

/** Restyle web copies only, preserving their data bindings, graphs and conditional lift colors. */
export function applyLegacyCustomWebGrid(view: CustomWebView): CustomWebView {
  const restyle = (document: LayoutDocument | null, header: boolean) => {
    if (!document) return null;
    const result = clone(document);
    const visit = (input: unknown) => {
      if (!input || typeof input !== "object") return;
      if (Array.isArray(input)) {
        input.forEach(visit);
        return;
      }
      const node = input as Record<string, unknown>;
      if (node.kind === "primitive" && node.primitive === "text") {
        const style = (node.style ?? {}) as Record<string, unknown>;
        node.style = {
          ...style,
          ...(header
            ? { color: "#ffffff", fontSize: 19, fontWeight: "700" }
            : !style.fontSize || style.fontSize === 14
              ? { fontSize: 18 }
              : {}),
        };
      }
      for (const [key, child] of Object.entries(node))
        if (key !== "behavior") visit(child);
    };
    visit(result.scene);
    return result;
  };
  return {
    ...view,
    presentation: createLegacyCustomWebPresentation(),
    columns: view.columns.map((c) => ({
      ...c,
      headerLayout: restyle(c.headerLayout, true),
      cellLayout: restyle(c.cellLayout, false),
    })),
  };
}

/** Optional Display Editor palette, assigned from unfiltered rows so search never changes category colors. */
export function customWebRowColors<T extends { id: string }>(
  view: CustomWebView,
  rows: T[],
  valueFor: (row: T, column: CustomWebColumn) => { text: string },
): Map<string, string> {
  const palette: string[] = [];
  const visit = (nodes: LayoutDocument["scene"]["children"]) =>
    nodes.forEach((node) => {
      if (/^category-color:\d+$/.test(node.id) && node.style?.backgroundColor)
        palette.push(node.style.backgroundColor);
      if ("children" in node) visit(node.children);
    });
  visit(view.presentation.scene.children);
  const colors = new Map<string, string>();
  const columns = view.categoryColumns
    .map((key) => view.columns.find((c) => c.key === key))
    .filter((c): c is CustomWebColumn => Boolean(c));
  if (!palette.length || !columns.length) return colors;
  const categories = new Map<string, string>();
  for (const row of rows) {
    const key = JSON.stringify(columns.map((c) => valueFor(row, c).text));
    if (!categories.has(key))
      categories.set(key, palette[categories.size % palette.length]);
    colors.set(row.id, categories.get(key)!);
  }
  return colors;
}
export function readCustomWebView(layouts: unknown): CustomWebView | null {
  try {
    const object = typeof layouts === "string" ? JSON.parse(layouts) : layouts;
    const view = (object as Record<string, unknown>)?.[CUSTOM_WEB_VIEW_KEY];
    return view && !validateCustomWebView(view)
      ? (view as CustomWebView)
      : null;
  } catch {
    return null;
  }
}
export function validateCustomWebView(input: unknown): string | null {
  try {
    const v = input as CustomWebView;
    if (
      !v ||
      v.version !== 1 ||
      !Array.isArray(v.columns) ||
      v.columns.length > 100 ||
      !Array.isArray(v.categoryColumns) ||
      !["LIVE", "CATEGORY"].includes(v.defaultMode) ||
      typeof v.followCurrentAthlete !== "boolean"
    )
      return "CUSTOM_DISCIPLINE_WEB_VIEW_INVALID";
    if (v.toolbar !== undefined && !validateCustomWebToolbar(v.toolbar))
      return "CUSTOM_DISCIPLINE_WEB_VIEW_INVALID";
    if (
      !validateLayout(v.presentation).valid ||
      !isValidCustomDisciplineBehavior(v.liveOrder) ||
      !isValidCustomDisciplineBehavior(v.categoryOrder)
    )
      return "CUSTOM_DISCIPLINE_WEB_VIEW_INVALID";
    const keys = new Set<string>();
    for (const c of v.columns) {
      if (
        !c ||
        !c.key?.trim() ||
        keys.has(c.key) ||
        !c.label?.trim() ||
        typeof c.fieldKey !== "string" ||
        typeof c.visible !== "boolean" ||
        (c.isSortable !== undefined && typeof c.isSortable !== "boolean") ||
        !Number.isFinite(c.width) ||
        c.width < 36 ||
        c.width > 1000 ||
        ![null, "left", "right"].includes(c.pin) ||
        !isValidCustomDisciplineBehavior(c.behavior)
      )
        return "CUSTOM_DISCIPLINE_WEB_VIEW_INVALID";
      if (
        (c.cellLayout && !validateLayout(c.cellLayout).valid) ||
        (c.headerLayout && !validateLayout(c.headerLayout).valid) ||
        (c.tooltipLayout && !validateLayout(c.tooltipLayout).valid)
      )
        return "CUSTOM_DISCIPLINE_WEB_VIEW_INVALID";
      keys.add(c.key);
    }
    if (v.categoryColumns.some((key) => !keys.has(key)))
      return "CUSTOM_DISCIPLINE_WEB_VIEW_INVALID";
    return null;
  } catch {
    return "CUSTOM_DISCIPLINE_WEB_VIEW_INVALID";
  }
}
/** Copy once in the admin editor. Subsequent editing-grid changes have no effect. */
export function createCustomWebView(
  schema: Pick<CustomDisciplineGroupType, "fields" | "displayLayouts">,
): CustomWebView {
  const fields = schema.fields
    .filter((f) => f.scope === "ATHLETE" && f.isVisible !== false)
    .sort((a, b) => a.order - b.order);
  return {
    version: 1,
    defaultMode: "LIVE",
    followCurrentAthlete: true,
    toolbar: createCustomWebToolbar(),
    liveOrder: null,
    categoryOrder: null,
    categoryColumns: [],
    presentation: (() => {
      const candidate = schema.displayLayouts
        ?.athlete_grid as unknown as LayoutDocument;
      const doc = candidate?.scene
        ? clone(candidate)
        : createLegacyCustomWebPresentation();
      doc.scene.children = doc.scene.children.filter(
        (n) =>
          ![
            "readonly-cell",
            "editable-cell",
            "editing-cell",
            "selected-cell",
          ].includes(n.id),
      );
      return doc;
    })(),
    columns: fields.map((f) => ({
      key: f.key,
      label: f.label,
      fieldKey: f.key,
      width: Math.max(36, Math.min(1000, f.cellLayout?.defaultWidth ?? 120)),
      visible: true,
      isSortable: false,
      pin: null as CustomWebColumn["pin"],
      behavior: f.behavior ? clone(f.behavior) : null,
      cellLayout: f.cellLayout?.layout
        ? (clone(f.cellLayout.layout) as unknown as LayoutDocument)
        : null,
      headerLayout: f.cellLayout?.headerLayout
        ? (clone(f.cellLayout.headerLayout) as unknown as LayoutDocument)
        : null,
      headerGroup: f.cellLayout?.headerGroup?.label,
    })),
  };
}
export function visibleCustomWebColumns(view: CustomWebView) {
  const columns = view.columns.filter((c) => c.visible);
  return [
    ...columns.filter((c) => c.pin === "left"),
    ...columns.filter((c) => !c.pin),
    ...columns.filter((c) => c.pin === "right"),
  ];
}
export function customWebColumnOffsets(columns: CustomWebColumn[]) {
  let left = 0,
    right = 0;
  const offsets: Record<string, { left?: number; right?: number }> = {};
  for (const c of columns)
    if (c.pin === "left") {
      offsets[c.key] = { left };
      left += c.width;
    }
  for (const c of [...columns].reverse())
    if (c.pin === "right") {
      offsets[c.key] = { right };
      right += c.width;
    }
  return offsets;
}
const programs = new WeakMap<
  object,
  ReturnType<typeof compileCustomDisciplineField>
>();
function program(behavior: CustomDisciplineBehavior, key: string) {
  let p = programs.get(behavior);
  if (!p) {
    p = compileCustomDisciplineField(key, normalizeBehaviorGraph(behavior));
    programs.set(behavior, p);
  }
  return p;
}
export function customWebCellValue(
  column: CustomWebColumn,
  input: CustomDisciplineEvaluateInput,
) {
  const value = (input.athlete as Record<string, unknown> | undefined)?.[
    column.fieldKey
  ];
  const text =
    value == null
      ? ""
      : typeof value === "object"
        ? String((value as { name?: unknown }).name ?? "")
        : String(value);
  if (!column.behavior) return { text, style: {} as Record<string, unknown> };
  return evaluateCustomDisciplineCell(program(column.behavior, column.key), {
    ...input,
    defaultText: text,
    currentValue: text,
  });
}
/** Ordering is a read-only projection; command effects other than view rows are ignored. */
export function customWebOrder<T extends { id: string }>(
  rows: T[],
  behavior: CustomDisciplineBehavior | null,
  input: CustomDisciplineEvaluateInput,
): T[] {
  if (!behavior) return rows;
  try {
    const compiled = program(behavior, "web-order");
    const initial = evaluateCustomDisciplineEvent(compiled, input);
    const clicked = evaluateCustomDisciplineActionClick(compiled, input);
    const result = { commands: [...initial.commands, ...clicked.commands] };
    const ids = result.commands
      .filter((c) => c.action === "customListSetViewRows")
      .slice(-1)[0]?.payload.list as { id?: string }[] | undefined;
    if (!Array.isArray(ids)) return rows;
    const byId = new Map(rows.map((r) => [r.id, r]));
    const ordered: T[] = [];
    for (const item of ids)
      if (item?.id && byId.has(item.id)) {
        ordered.push(byId.get(item.id)!);
        byId.delete(item.id);
      }
    return [...ordered, ...byId.values()];
  } finally {
    if (input.instanceKey)
      releaseCustomDisciplineBehaviorRuntime(input.instanceKey);
  }
}

const readonlyDocuments = new WeakMap<LayoutDocument, LayoutDocument>();
/** Keep spectator controls non-editable, including controls inside list templates. */
export function readOnlyCustomWebDocument(
  document: LayoutDocument,
): LayoutDocument {
  const cached = readonlyDocuments.get(document);
  if (cached) return cached;
  const visit = (input: unknown): unknown => {
    if (Array.isArray(input)) return input.map(visit);
    if (!input || typeof input !== "object") return input;
    const source = input as Record<string, unknown>;
    const result = Object.fromEntries(
      Object.entries(source).map(([key, value]) => [
        key,
        key === "behavior" ? value : visit(value),
      ]),
    );
    if (
      source.kind === "component" &&
      ["customDisciplineInput", "customDisciplineButton"].includes(
        String(source.component),
      )
    )
      result.props = {
        ...(result.props as object),
        readOnly: true,
        disabled: true,
        isDisabled: true,
      };
    return result;
  };
  const result = visit(document) as LayoutDocument;
  readonlyDocuments.set(document, result);
  return result;
}
